import React, { useEffect, useRef } from 'react';
import { EditorView, keymap, lineNumbers, highlightActiveLine, highlightActiveLineGutter } from '@codemirror/view';
import { EditorState, EditorSelection, Compartment } from '@codemirror/state';
import { defaultKeymap, indentWithTab, history, historyKeymap } from '@codemirror/commands';
import { json } from '@codemirror/lang-json';
import { xml } from '@codemirror/lang-xml';
import { javascript } from '@codemirror/lang-javascript';
import { indentOnInput, syntaxHighlighting, HighlightStyle } from '@codemirror/language';
import { lintGutter, setDiagnostics, Diagnostic } from '@codemirror/lint';
import { tags } from '@lezer/highlight';
import { createBlazeMeterExtension } from './codemirrorBlazeMeterScript';
import { BodyValidationError } from 'types/rest/bodyValidationTypes';

interface ResponseBodyEditorProps {
  value: string;
  language: string;
  readOnly: boolean;
  height: number;
  validationErrors: BodyValidationError[];
  onValidationErrorsDismiss: () => void;
  onChange: (value: string) => void;
  onBlur: () => void;
  isInvalid: boolean;
  onEditorView?: (view: EditorView | null) => void;
}

const textmateHighlightStyle = HighlightStyle.define([
  { tag: tags.keyword, color: '#0000ff' },
  { tag: tags.string, color: '#008000' },
  { tag: tags.comment, color: '#808080', fontStyle: 'italic' },
  { tag: tags.number, color: '#098658' },
  { tag: tags.bool, color: '#0000ff' },
  { tag: tags.null, color: '#0000ff' },
  { tag: tags.propertyName, color: '#0451a5' },
  { tag: tags.attributeName, color: '#e50000' },
  { tag: tags.tagName, color: '#800000' },
]);

const baseTheme = EditorView.theme({
  '&': { height: '100%', fontSize: '13px', backgroundColor: '#fff' },
  '.cm-content': { fontFamily: "'Consolas', 'Monaco', 'Courier New', monospace", padding: '4px 0' },
  '.cm-gutters': { backgroundColor: '#f5f5f5', borderRight: '1px solid #ddd', color: '#999' },
  '.cm-activeLine': { backgroundColor: '#f0f7ff' },
  '.cm-activeLineGutter': { backgroundColor: '#e8f2ff' },
  '.cm-focused': { outline: 'none' },
  '.cm-scroller': { overflow: 'auto', height: '100%' },
  '.cm-lint-marker-error': { content: '' },
  // Force BlazeMeter script delimiters (${#script...} and ${/script}) to black,
  // overriding any syntax highlighting applied to child token spans.
  '.cm-bzm-delimiter *': { color: '#0451a5 !important' },
  '.cm-bzm-delimiter': { color: '#0451a5' },
});

const insertNewlineKeepIndent = (view: EditorView): boolean => {
  const { state } = view;
  const changes = state.changeByRange(range => {
    const line = state.doc.lineAt(range.from);
    const indent = line.text.match(/^(\s*)/)?.[1] ?? '';
    const insert = '\n' + indent;
    return {
      changes: { from: range.from, to: range.to, insert },
      range: EditorSelection.cursor(range.from + insert.length),
    };
  });
  view.dispatch(state.update(changes, { scrollIntoView: true, userEvent: 'input' }));
  return true;
};

function getLanguageExtension(language: string) {
  switch (language) {
    case 'json':
      return [json()];
    case 'xml':
      return [xml()];
    case 'javascript':
      return [javascript()];
    case 'blazemeter-script':
      return createBlazeMeterExtension();
    default:
      return [];
  }
}

const ResponseBodyEditor: React.FC<ResponseBodyEditorProps> = ({
  value,
  language,
  readOnly,
  height,
  validationErrors,
  onValidationErrorsDismiss,
  onChange,
  onBlur,
  isInvalid,
  onEditorView,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const viewRef = useRef<EditorView | null>(null);
  const languageCompartment = useRef(new Compartment());
  const readOnlyCompartment = useRef(new Compartment());
  // Keep callbacks in refs so the editor setup effect doesn't need to re-run when they change
  const onChangeRef = useRef(onChange);
  const onBlurRef = useRef(onBlur);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);
  useEffect(() => {
    onBlurRef.current = onBlur;
  }, [onBlur]);

  // Create the editor once on mount
  useEffect(() => {
    if (!containerRef.current) return;

    const state = EditorState.create({
      doc: value,
      extensions: [
        lineNumbers(),
        highlightActiveLine(),
        highlightActiveLineGutter(),
        indentOnInput(),
        EditorView.lineWrapping,
        history(),
        keymap.of([{ key: 'Enter', run: insertNewlineKeepIndent }, ...defaultKeymap, ...historyKeymap, indentWithTab]),
        languageCompartment.current.of(getLanguageExtension(language)),
        readOnlyCompartment.current.of(EditorState.readOnly.of(readOnly)),
        syntaxHighlighting(textmateHighlightStyle),
        baseTheme,
        lintGutter(),
        EditorView.updateListener.of(update => {
          if (update.docChanged) {
            onChangeRef.current(update.state.doc.toString());
          }
        }),
        EditorView.domEventHandlers({
          blur: () => {
            onBlurRef.current();
            return false;
          },
        }),
        EditorState.tabSize.of(2),
      ],
    });

    const view = new EditorView({ state, parent: containerRef.current });
    viewRef.current = view;
    onEditorView?.(view);

    return () => {
      view.destroy();
      viewRef.current = null;
      onEditorView?.(null);
    };
  }, []);

  // Sync external value changes into the editor
  useEffect(() => {
    const view = viewRef.current;
    if (!view || value === view.state.doc.toString()) return;
    view.dispatch({
      changes: { from: 0, to: view.state.doc.length, insert: value },
      selection: EditorSelection.cursor(0),
    });
  }, [value]);

  // Reconfigure language when it changes
  useEffect(() => {
    viewRef.current?.dispatch({
      effects: languageCompartment.current.reconfigure(getLanguageExtension(language)),
    });
  }, [language]);

  // Reconfigure readOnly when it changes
  useEffect(() => {
    viewRef.current?.dispatch({
      effects: readOnlyCompartment.current.reconfigure(EditorState.readOnly.of(readOnly)),
    });
  }, [readOnly]);

  // Push validation errors into the editor as lint diagnostics
  useEffect(() => {
    const view = viewRef.current;
    if (!view) return;
    const diagnostics: Diagnostic[] = validationErrors.map(err => {
      const lineNum = Math.min(Math.max(err.line, 1), view.state.doc.lines);
      const lineInfo = view.state.doc.line(lineNum);
      return {
        from: lineInfo.from,
        to: lineInfo.to,
        severity: err.severity === 'error' ? 'error' : 'warning',
        message: err.message,
      };
    });
    view.dispatch(setDiagnostics(view.state, diagnostics));
  }, [validationErrors]);

  return (
    <div>
      <div
        style={{
          border: isInvalid ? '1px solid #dc3545' : '1px solid #ced4da',
          borderRadius: 4,
          overflow: 'hidden',
          height: `${height}px`,
        }}
        ref={containerRef}
      />

      {validationErrors.length > 0 && (
        <div className="alert alert-danger alert-dismissible mt-2 mb-0 py-2" role="alert">
          {validationErrors.map((err, idx) => (
            <div key={idx}>
              Line {err.line}: {err.message}
            </div>
          ))}
          <button
            type="button"
            className="close"
            style={{ position: 'absolute', top: '4px', right: '8px' }}
            aria-label="Close"
            onClick={onValidationErrorsDismiss}
          >
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
      )}
    </div>
  );
};

export default ResponseBodyEditor;
